/**
 * Toast + Toaster — Base UI Toast primitive, styled to match Webex's
 * `md-alert` component (bottom-right viewport).
 *
 * Mount <Toaster /> once near the root. Trigger via `toast`:
 *
 *   toast.success("Saved.")
 *   toast.error("Couldn't save.", { description: "Try again later." })
 *   toast.show({ title: "Custom", variant: "info",
 *                action: { label: "Show More", onClick: () => {} } })
 *
 * Design system mapping (verified against admin.webex.com `md-alert-container`):
 *   • Card: bg-white dark:bg-webex-gray-950
 *           border 1px rgba(0,0,0,0.20) → border-webex-gray-300 dark:border-webex-gray-700
 *           rounded-md (8px)
 *           shadow 0 8px 16px 0 rgba(0,0,0,0.11)  px-4 py-3 w-96
 *   • Icon: variant-colored outline circle, size-6
 *   • Content: message text with inline "Show More" action as underlined link
 *   • Close: 24px icon button top-right
 *   • No left-border accent stripe.
 */
import * as React from "react"
import { Toast as BaseToast } from "@base-ui-components/react/toast"
import { cn } from "@lib/cn"

type ToastVariant = "default" | "info" | "success" | "warning" | "error"

interface ToastData {
  variant?: ToastVariant
  action?: { label: React.ReactNode; onClick: () => void }
}

const manager = BaseToast.createToastManager()

const iconColor: Record<ToastVariant, string> = {
  default: "text-webex-gray-700 dark:text-webex-gray-300",
  info:    "text-webex-blue-600 dark:text-webex-blue-400",
  success: "text-webex-green-600 dark:text-webex-green-400",
  warning: "text-webex-yellow-600 dark:text-webex-yellow-400",
  error:   "text-webex-red-600 dark:text-webex-red-400",
}

function VariantIcon({ variant }: { variant: ToastVariant }) {
  const common = "size-6 shrink-0"
  switch (variant) {
    case "success":
      return (
        <svg viewBox="0 0 24 24" className={cn(common, iconColor[variant])} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <polyline points="7.5,12.5 10.5,15.5 16.5,9" />
        </svg>
      )
    case "error":
      return (
        <svg viewBox="0 0 24 24" className={cn(common, iconColor[variant])} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" />
        </svg>
      )
    case "warning":
      return (
        <svg viewBox="0 0 24 24" className={cn(common, iconColor[variant])} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 3.5L21 19H3z" />
          <path d="M12 10v4.5" />
          <circle cx="12" cy="17" r="0.6" fill="currentColor" />
        </svg>
      )
    case "info":
      return (
        <svg viewBox="0 0 24 24" className={cn(common, iconColor[variant])} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5.5" />
          <circle cx="12" cy="7.75" r="0.6" fill="currentColor" />
        </svg>
      )
    default:
      return (
        <svg viewBox="0 0 24 24" className={cn(common, iconColor[variant])} fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
          <circle cx="12" cy="12" r="9" />
        </svg>
      )
  }
}

/** Mount once at the root of the app to render any toasts the manager emits. */
export function Toaster() {
  return (
    <BaseToast.Provider toastManager={manager}>
      <BaseToast.Portal>
        <BaseToast.Viewport className="fixed bottom-6 right-6 z-50 flex w-96 max-w-[calc(100vw-3rem)] flex-col gap-2 outline-none">
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  )
}

function ToastList() {
  const { toasts } = BaseToast.useToastManager()
  return (
    <>
      {toasts.map((t) => {
        const data = (t.data ?? {}) as ToastData
        const variant = data.variant ?? "default"
        return (
          <BaseToast.Root
            key={t.id}
            toast={t}
            className={cn(
              "relative rounded-md border bg-white dark:bg-webex-gray-950",
              "border-webex-gray-300 dark:border-webex-gray-700",
              "shadow-[0_8px_16px_0_rgba(0,0,0,0.11)] dark:shadow-[0_8px_16px_0_rgba(0,0,0,0.5)]",
              "px-4 py-3 pr-10 text-sm text-webex-gray-1000 dark:text-white",
              "data-[starting-style]:opacity-0 data-[starting-style]:translate-x-4",
              "data-[ending-style]:opacity-0 data-[ending-style]:translate-x-4",
              "transition-[opacity,transform] duration-200 motion-reduce:transition-none",
              "outline-none",
            )}
          >
            <div className="flex items-start gap-3">
              <VariantIcon variant={variant} />
              <div className="flex-1 min-w-0">
                {t.title && (
                  <BaseToast.Title className="font-medium text-webex-gray-1000 dark:text-white">
                    {t.title}
                  </BaseToast.Title>
                )}
                <BaseToast.Description className="text-sm text-webex-gray-1000 dark:text-white leading-snug">
                  {t.description}
                  {data.action && (
                    <>
                      {t.description ? " " : null}
                      <BaseToast.Action
                        onClick={data.action.onClick}
                        className={cn(
                          "inline underline underline-offset-2",
                          "text-webex-blue-700 dark:text-webex-blue-400",
                          "hover:text-webex-blue-900 dark:hover:text-webex-blue-200",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 rounded",
                        )}
                      >
                        {data.action.label}
                      </BaseToast.Action>
                    </>
                  )}
                </BaseToast.Description>
              </div>
            </div>
            <BaseToast.Close
              aria-label="Close"
              className={cn(
                "absolute top-2.5 right-2.5 inline-flex size-6 items-center justify-center rounded",
                "text-webex-gray-700 dark:text-webex-gray-300",
                "hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850 hover:text-webex-gray-1000 dark:hover:text-white",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600",
              )}
            >
              <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </BaseToast.Close>
          </BaseToast.Root>
        )
      })}
    </>
  )
}

interface ToastShowOptions {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  duration?: number
  priority?: "low" | "high"
  action?: ToastData["action"]
}

function show(options: ToastShowOptions) {
  return manager.add({
    title: options.title,
    description: options.description,
    timeout: options.duration ?? 5000,
    priority: options.priority,
    data: { variant: options.variant ?? "default", action: options.action },
  })
}

const variantHelper = (variant: ToastVariant) =>
  (title: React.ReactNode, opts?: Omit<ToastShowOptions, "variant" | "title">) =>
    show({ ...opts, title, variant })

export const toast = {
  show,
  default: variantHelper("default"),
  info: variantHelper("info"),
  success: variantHelper("success"),
  warning: variantHelper("warning"),
  error: variantHelper("error"),
  close: (id: string) => manager.close(id),
}
